import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { forkJoin, Subject, map, takeUntil } from 'rxjs';
import { AdminService } from 'src/app/services/admin.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';

@Component({
  selector: 'ness-manage-leadership',
  templateUrl: './manage-leadership.component.html',
  styles: [
  ]
})
export class ManageLeadershipComponent implements OnInit, OnDestroy {
  fdLeadershipSource: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[] = [];
  fdLeadershipTarget: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[] = [];
  saveLeadershipDisabled: boolean = true;
  leadershipIsInvalid: boolean = true;
  showConfirmDialog: boolean = false;

  private _destroyed$: Subject<void> = new Subject();

  constructor(private adminService: AdminService,
    public messageService: MessageService) { }

  ngOnInit(): void {
    this._loadAssignedFDLeadership();
    this._loadUnassignedFDLeadership();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.messageService.clear('manageFDLeadersMsg');
  }

  private _validateFDLeadership(): void {

    const assignedFieldDivisions: any[] = AppUtils.arrayGroupBy<ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO>(this.fdLeadershipTarget, 'LeadershipFieldDivisionID');

    this.leadershipIsInvalid = Object.keys(assignedFieldDivisions).length < AppConstants.fieldDivisionCount;
    this.saveLeadershipDisabled = this.leadershipIsInvalid;
  }

  private _sortLeadership(sortTarget: string, primaryKey: string, secondaryKey: string): void {
    let sortedLeadership = [] as ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[];
    const sortFn = (item1: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO, item2: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO) => item1[primaryKey].toString().localeCompare(item2[primaryKey].toString()) || item1[secondaryKey].toString().localeCompare(item2[secondaryKey].toString());

    if (sortTarget == 'source') {
      sortedLeadership = this.fdLeadershipSource.sort(sortFn);
      this.fdLeadershipSource = [...sortedLeadership];
    }

    if (sortTarget == 'target') {
      sortedLeadership = this.fdLeadershipTarget.sort(sortFn);
      this.fdLeadershipTarget = [...sortedLeadership];
    }
  }

  private _loadAssignedFDLeadership(): void {
    this.adminService.getAssignedFDLeadership()
      .pipe(
        takeUntil(this._destroyed$),
        map((leadership: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[]) => {
          this.fdLeadershipTarget = [...leadership];
          this._validateFDLeadership();
          this.saveLeadershipDisabled = true;
        })
      ).subscribe({
        error: (err:any) => this.messageService.add({ key: 'manageFDLeadersMsg', severity: 'error', summary: 'Error', detail: err.error || err.error.ExceptionMessage || err.error.Message })
      });
  }

  private _loadUnassignedFDLeadership(): void {
    this.adminService.getUnassignedFDLeadership()
      .pipe(
        takeUntil(this._destroyed$),
        map((leadership: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[]) => {
          this.fdLeadershipSource = [...leadership];
          this.saveLeadershipDisabled = true;
        })
      ).subscribe({
        error: (err:any) => this.messageService.add({ key: 'manageFDLeadersMsg', severity: 'error', summary: 'Error', detail: err.error || err.error.ExceptionMessage || err.error.Message })
      });
  }

  public assignLeadership(evt: { items: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[] }): void {
    this.saveLeadershipDisabled = false;
    this._validateFDLeadership();
    this._sortLeadership('target', 'FieldDivisionName', 'LastName');
  }

  public unassignLeadership(evt: { items: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[] }): void {
    this.saveLeadershipDisabled = false;
    this._validateFDLeadership();
    this._sortLeadership('source', 'FieldDivisionName', 'LastName');
  }

  public saveLeadershipChanges(): void {
    let removedItems: ServerModels.ENESS_XREF_FieldDivisionLeadership[] = this.fdLeadershipSource.filter(x => !!x.FieldDivisionLeadershipID).map(m => {
      return <ServerModels.ENESS_XREF_FieldDivisionLeadership>{
        FieldDivisionLeadershipID: m.FieldDivisionLeadershipID,
        FieldDivisionLeadershipUserID: m.FieldDivisionLeadershipUserID,
        LeadershipFieldDivisionID: m.LeadershipFieldDivisionID,
        ApplicationUserJobTitlesID: m.ApplicationUserJobTitlesID
      };
    });

    let assignedItems: ServerModels.ENESS_XREF_FieldDivisionLeadership[] = this.fdLeadershipTarget.map(m => {
      return <ServerModels.ENESS_XREF_FieldDivisionLeadership>{
        FieldDivisionLeadershipID: m.FieldDivisionLeadershipID,
        FieldDivisionLeadershipUserID: m.FieldDivisionLeadershipUserID,
        LeadershipFieldDivisionID: m.LeadershipFieldDivisionID,
        ApplicationUserJobTitlesID: m.ApplicationUserJobTitlesID
      };
    });

    forkJoin(
      this.adminService.assignFieldDivisionLeadership(assignedItems)
    ).pipe(takeUntil(this._destroyed$)).subscribe({
      complete: () => {
        this.showConfirmDialog = true;
        this._loadAssignedFDLeadership();
        this._loadUnassignedFDLeadership();
      },
      error: (err:any) => this.messageService.add({ key: 'manageFDLeadersMsg', severity: 'error', summary: 'Error', detail: err.error || err.error.ExceptionMessage || err.error.Message })
    });

  }

  public sourceTrackByFn(index: number, el: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO): string {
    return el.ApplicationUserJobTitlesID;
  }

  public targetTrackByFn(index: number, el: ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO): string {
    return el.FieldDivisionLeadershipID;
  }
}
